"use client";

import type { ReactNode } from "react";
import { AnimatedCounter, DividerRow, ScrollReveal } from "../shared";
import type { DividerRowItem } from "../shared";
import { CubeIcon, DollarIcon, ShieldCheckIcon } from "../shared/icons";

type StatItem = {
  label: string;
  render: () => ReactNode;
};

const STATS: StatItem[] = [
  {
    label: "Live Monitoring",
    render: () => (
      <span className="flex items-center min-w-0 gap-2.5">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: "#22c55e", boxShadow: "0 0 8px rgba(34,197,94,0.6)" }}
          aria-hidden="true"
        />
        <span>
          <span className="block text-lg font-extrabold leading-tight" style={{ color: "#f1f5f9" }}>
            24/7
          </span>
          <span className="block text-xs font-medium leading-tight" style={{ color: "var(--muted-text, #94a3b8)" }}>
            Live Monitoring
          </span>
        </span>
      </span>
    ),
  },
  {
    label: "Blockchains",
    render: () => (
      <span className="flex items-center min-w-0 gap-2.5">
        <CubeIcon size={18} className="shrink-0" style={{ color: "#3B82F6" }} />
        <span>
          <AnimatedCounter value={50} suffix="+" className="block text-lg font-extrabold leading-tight text-[#f1f5f9]" />
          <span className="block text-xs font-medium leading-tight" style={{ color: "var(--muted-text, #94a3b8)" }}>
            Blockchains
          </span>
        </span>
      </span>
    ),
  },
  {
    label: "Donations Tracked",
    render: () => (
      <span className="flex items-center min-w-0 gap-2.5">
        <DollarIcon size={18} className="shrink-0" style={{ color: "#22c55e" }} />
        <span>
          <AnimatedCounter
            value={12.7}
            decimals={1}
            prefix="$"
            suffix="M+"
            className="block text-lg font-extrabold leading-tight text-[#f1f5f9]"
          />
          <span className="block text-xs font-medium leading-tight" style={{ color: "var(--muted-text, #94a3b8)" }}>
            Donations Tracked
          </span>
        </span>
      </span>
    ),
  },
  {
    label: "Chains Supported",
    render: () => (
      <span className="flex items-center min-w-0 gap-2.5">
        <ShieldCheckIcon size={18} className="shrink-0" style={{ color: "#5B7FFF" }} />
        <span>
          <AnimatedCounter value={15} suffix="+" className="block text-lg font-extrabold leading-tight text-[#f1f5f9]" />
          <span className="block text-xs font-medium leading-tight" style={{ color: "var(--muted-text, #94a3b8)" }}>
            Chains Supported
          </span>
        </span>
      </span>
    ),
  },
];

const STAT_ITEMS: DividerRowItem[] = STATS.map((s) => ({
  key: s.label,
  content: s.render(),
}));

/**
 * Platform stats strip near the bottom of the homepage: no heading, single
 * bordered glass row divided into 4 columns by vertical lines (not
 * individually-bordered tiles).
 *
 * Values (24/7 Live Monitoring, 50+ Blockchains, $12.7M+ Donations Tracked,
 * 15+ Chains Supported) intentionally match the original client-approved
 * "Blockpage411 Homepage" mockup's StatsStrip exactly, per explicit request -
 * superseding the later 2026-06-25 pixel reference's higher numbers
 * (75+/$18.6M+/25+), which are no longer used here.
 */
export default function StatsSection() {
  return (
    <section aria-label="Platform stats" className="page-section relative w-full py-8 sm:py-10">
      <ScrollReveal className="mx-auto max-w-6xl">
        <DividerRow items={STAT_ITEMS} />
      </ScrollReveal>
    </section>
  );
}
